import React, { useEffect, useState } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { useParams, useNavigate } from 'react-router-dom';

export const AdminEditStrengthExercise = () => {
  const { staff } = useStaffAuthContext();
  const { exercise_id } = useParams();
  const navigate = useNavigate(); // Initialize useNavigate

  const [title, setTitle] = useState("");
  const [instructions, setInstructions] = useState("");
  const [video_url, setVideoUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const fetchExercise = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_URL.GET_STRENGTH_EXERCISE}${exercise_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${staff.token}`,
          },
        });
  
        const data = await response.json();

        if (!response.ok) {
          setError(data.error || "Failed to fetch exercise details");
          return;
        }
  
        setTitle(data.exercise.title || "");
        setInstructions(data.exercise.instructions || "");
        setVideoUrl(data.exercise.video_url || "");
      } catch (err) {
        setError("An error occurred while fetching the exercise.");
      } finally {
        setIsLoading(false);
      }
    };
  
    fetchExercise();
  }, [exercise_id, staff.token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL.UPDATE_STRENGTH_EXERCISE}${exercise_id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify({ title, instructions, video_url }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Failed to edit exercise");
        return;
      }

      setSuccessMessage("Exercise edited successfully!");
    } catch (err) {
      setError("An error occurred while saving the exercise.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-screen">
      <StaffSideMenu />
      <div className="flex-1 p-6 bg-gray-100 pt-16">
        <div className="flex justify-end mb-4">
          {/* Go Back Button using useNavigate */}
          <button
            onClick={() => navigate(-1)} // Navigate back to the previous page
            className="inline-flex items-center px-4 py-2 bg-red-600 text-white font-semibold rounded-md shadow hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Go Back
          </button>
        </div>

        <h1 className="text-2xl font-bold mb-6">Edit Strength Exercise</h1>

        <div className="flex space-x-8">
          <div className="flex-1">
            <form
              onSubmit={handleSubmit}
              className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4 max-w-lg mx-auto"
            >
              <div className="mb-4">
                <label
                  htmlFor="title"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Exercise Title*
                </label>
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter exercise title"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  required
                />
              </div>
              <div className="mb-4">
                <label
                  htmlFor="instructions"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Exercise Instructions*
                </label>
                <textarea
                  id="instructions"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Enter exercise instructions"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  required
                />
              </div>
              <div className="mb-4">
                <label
                  htmlFor="video_url"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  YouTube Video URL
                </label>
                <input
                  type="text"
                  id="video_url"
                  value={video_url}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="Enter YouTube embed URL"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
              {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
              {successMessage && <p className="text-green-500 text-sm mb-4">{successMessage}</p>}
              <div className="flex items-center justify-between">
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`${
                    isLoading ? "bg-blue-300" : "bg-blue-500 hover:bg-blue-700"
                  } text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline`}
                >
                  {isLoading ? "Saving..." : "Save Exercise"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
